import React from 'react';
import PropTypes from 'prop-types';
import { Map } from 'immutable';
import { Col, Row } from 'react-bootstrap';
import cn from 'classnames';
import moment from 'moment-timezone';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

import CheckoutController from '../../../event_components/Checkout';
import Title from '../../../event_components/title';
import Details from '../../../event_components/details';
import AttendButton from '../../../event_components/attend_button';
import EventStatus from '../../../event_components/event_status';
import TicketPurchasing from '../../../event_components/ticket-purchasing';
import AddEventToCalendar from '../../../event_components/add_to_calendar';
import TwitchPreviousVideo from '../../../event_components/twitch-video';
import { EventTime } from '../../../event_components/event-time';
import { EventDate } from '../../../event_components/event-date';
import Gallery from '../../shared/gallery';
import { LocationItem } from '../../shared/location-item/location-item';
import HostedBy from '../../../event_components/hosted-by';
import Attendees from '../../../event_components/attendees';
import SubmitEventButton from '../../../event_components/submit-event-button';
import { LocaleWrapper } from '../../../../locale-wrapper';
import { attendButtonIsVisible } from '../../shared/utils';
import DateRangeIcon from '@mui/icons-material/DateRange';
import WatchLaterSharpIcon from '@mui/icons-material/WatchLaterSharp';

import styles from './monthly-view.scss';

const propTypes = {
  event: PropTypes.instanceOf(Map).isRequired,
  settings: PropTypes.instanceOf(Map).isRequired,
};

/**
 * MonthHeader component displays the month and year header
 */
function MonthHeader({ month, year }) {
  return (
    <div className={styles.monthHeader}>
      <h2>
        {month} {year}
      </h2>
    </div>
  );
}

MonthHeader.propTypes = {
  month: PropTypes.string.isRequired,
  year: PropTypes.string.isRequired,
};

/**
 * DateHeader component displays the specific date with day of week
 */
function DateHeader({ date }) {
  return (
    <div className={styles.dateHeader}>
      <h3>{date}</h3>
    </div>
  );
}

DateHeader.propTypes = {
  date: PropTypes.string.isRequired,
};

/**
 * MonthlyViewEvent component displays a single event
 */
export class MonthlyViewEvent extends React.Component {
  constructor() {
    super();
    this.state = {
      openTicketing: false,
    };
  }

  toggleAttendButton = () => {
    const { event, onEventRegister } = this.props;

    if (event.get('type') === 'google') {
      return;
    }
    this.props.onResizeComponent();
    this.setState(
      {
        openTicketing: onEventRegister ? false : !this.state.openTicketing,
      },
      () => this.props.onResizeComponent()
    );
    onEventRegister && onEventRegister(event);
  };

  render() {
    const { event, settings, onResizeComponent } = this.props;
    const logo = event.getIn(['logo', 'url']);
    const hasLogo = settings.get('showEventLogo');

    if (event.get('initial_embed') || event.get('is_offline')) {
      return event.get('is_offline') ? (
        <TwitchPreviousVideo settings={settings} event={event} />
      ) : (
        <TicketPurchasing event={event} settings={settings} open onResizeComponent={onResizeComponent} />
      );
    }

    const xsLogoVisible = logo && hasLogo && event.get('type') !== 'google' && event.get('type') !== 'zoom';

    return (
      <div className={cn(styles.eventContainer, { [styles.open]: this.state.openTicketing })}>
        <Row className={cn(styles.eventRow, 'event', { [styles.open]: this.state.openTicketing })}>
          <div className={styles.eventWrapper}>
            <div className={cn(styles.infoWrapper, { [styles.open]: this.state.openTicketing })}>
              <Col xs={this.state.openTicketing ? null : 5} className={styles.gallery}>
                {xsLogoVisible && <Gallery event={event} />}
              </Col>

              <Col className={styles.details} xs={this.state.openTicketing ? null : 7}>
                <Title event={event} settings={settings} />
                <div className={styles.eventFactsList}>
                  <LocationItem event={event} settings={settings} className={styles.locationItem} />
                  <div className={styles.factItem}>
                    <DateRangeIcon alt="date" width="20px" className="date" />
                    <EventDate event={event} settings={settings} />
                  </div>
                  <div className={styles.factItem}>
                    <WatchLaterSharpIcon alt="time" width="20px" className="date" />
                    <EventTime event={event} settings={settings} />
                  </div>
                </div>

                {settings.get('showEventStatus') && <EventStatus event={event} settings={settings} />}
                {settings.get('enableAddToCalendar') && <AddEventToCalendar event={event} settings={settings} className={styles.addToCalendar} />}
                <Details event={event} settings={settings} className={styles.description} onResizeComponent={onResizeComponent} />

                {/* New components for Hosted By and Attendees */}
                <HostedBy event={event} settings={settings} className={styles.hostedBy} />
                <Attendees event={event} settings={settings} className={styles.attendees} />
              </Col>
            </div>
            {this.state.openTicketing && (
              <div className={styles.checkoutWrapper}>
                <CheckoutController
                  variant="redesign"
                  event={event}
                  settings={settings}
                  inClassName="checkout-fade"
                  openTicketing={this.state.openTicketing}
                  onResizeComponent={onResizeComponent}
                  onClose={() => {
                    this.setState({
                      openTicketing: false,
                    });
                  }}
                />
              </div>
            )}
          </div>
          {attendButtonIsVisible(event, settings, this.state.openTicketing) && (
            <Col xs={12} className={styles.attendButtonWrapper}>
              <AttendButton
                isFullWidth
                event={event}
                settings={settings}
                handleButtonClick={this.toggleAttendButton}
                className={styles.centeredButton}
              />
            </Col>
          )}
        </Row>
      </div>
    );
  }
}

MonthlyViewEvent.propTypes = propTypes;

/**
 * MonthlyView main component with calendar sidebar
 */
export default class MonthlyView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedDate: null,
      showCalendarModal: false,
    };
    this.popoverRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  /**
   * Get event dates for calendar highlighting
   */
  getEventDates = () => {
    const { events } = this.props;
    const dates = [];

    events.forEach((event) => {
      const startTime = event.getIn(['date', 'start', 'utc']);
      const timezone = event.getIn(['date', 'start', 'timezone']) || moment.tz.guess();
      const momentDate = moment(startTime).tz(timezone);
      dates.push(momentDate.format('YYYY-MM-DD'));
    });

    return dates;
  };

  /**
   * Filter events based on selected date
   */
  getFilteredEvents = () => {
    const { events } = this.props;
    const { selectedDate } = this.state;

    if (!selectedDate) {
      return events;
    }

    const selectedDateStr = moment(selectedDate).format('YYYY-MM-DD');

    return events.filter((event) => {
      const startTime = event.getIn(['date', 'start', 'utc']);
      const timezone = event.getIn(['date', 'start', 'timezone']) || moment.tz.guess();
      const eventDateStr = moment(startTime).tz(timezone).format('YYYY-MM-DD');
      return eventDateStr === selectedDateStr;
    });
  };

  handleClickOutside = (event) => {
    if (this.popoverRef.current && !this.popoverRef.current.contains(event.target)) {
      this.setState({ showCalendarModal: false });
    }
  };
  /**
   * Check if a date has events
   */
  hasEvents = (date) => {
    const eventDates = this.getEventDates();
    const dateStr = moment(date).format('YYYY-MM-DD');
    return eventDates.includes(dateStr);
  };

  /**
   * Handle calendar date click
   */
  handleDateClick = (date) => {
    this.setState({ selectedDate: date });
  };

  /**
   * Group events by month
   */
  groupEventsByMonth = (events) => {
    const grouped = {};

    events.forEach((event) => {
      const startTime = event.getIn(['date', 'start', 'utc']);
      const timezone = event.getIn(['date', 'start', 'timezone']) || moment.tz.guess();
      const locale = event.get('locale', 'en').split('_')[0];
      const momentDate = moment(startTime).tz(timezone).locale(locale);

      const monthKey = momentDate.format('YYYY-MM');

      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          month: momentDate.format('MMMM'),
          year: momentDate.format('YYYY'),
          events: [],
        };
      }

      grouped[monthKey].events.push(event);
    });

    return grouped;
  };

  /**
   * Group events by date within a month
   */
  groupEventsByDate = (events) => {
    const grouped = {};

    events.forEach((event) => {
      const startTime = event.getIn(['date', 'start', 'utc']);
      const timezone = event.getIn(['date', 'start', 'timezone']) || moment.tz.guess();
      const locale = event.get('locale', 'en').split('_')[0];
      const momentDate = moment(startTime).tz(timezone).locale(locale);

      const dateKey = momentDate.format('YYYY-MM-DD');
      const dateDisplay = momentDate.format('D MMM YYYY dddd');

      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateDisplay,
          events: [],
        };
      }

      grouped[dateKey].events.push(event);
    });

    return grouped;
  };

  render() {
    const { settings, onResizeComponent, onEventRegister } = this.props;
    const filteredEvents = this.getFilteredEvents();
    const monthGroups = this.groupEventsByMonth(filteredEvents);

    return (
      <div className={styles.container}>
        {/* Mobile Calendar Popover */}
        <div className={styles.mobileCalendarPopover} ref={this.popoverRef}>
          <button className={styles.filterButton} onClick={() => this.setState({ showCalendarModal: !this.state.showCalendarModal })}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="6" width="18" height="15" rx="2" stroke="currentColor" strokeWidth="2" />
              <path d="M3 10h18" stroke="currentColor" strokeWidth="2" />
              <path d="M8 3v6M16 3v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Calendar
          </button>

          {this.state.showCalendarModal && (
            <div className={styles.popoverContent}>
              <div className={styles.popoverHeader}>
                <h2>Select Date</h2>
                <button className={styles.popoverClose} onClick={() => this.setState({ showCalendarModal: false })} aria-label="Close">
                  ×
                </button>
              </div>
              <div className={styles.popoverBody}>
                <div className={styles.popoverCalendarWrapper}>
                  <SubmitEventButton settings={settings} />
                  <div className={styles.calendarWrapper}>
                    <Calendar
                      onChange={(date) => {
                        this.handleDateClick(date);
                        this.setState({ showCalendarModal: false });
                      }}
                      value={this.state.selectedDate}
                      tileClassName={({ date }) => (this.hasEvents(date) ? `react-calendar-has-events ${styles.hasEvents}` : null)}
                      className={styles.calendar}
                    />
                    {this.state.selectedDate && (
                      <button
                        className={styles.clearButton}
                        onClick={() => {
                          this.setState({ selectedDate: null, showCalendarModal: false });
                        }}
                      >
                        Show All Events
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <Row className={styles.mainRow}>
          {/* Left side: Events grouped by month and date */}
          <Col xs={8} className={styles.eventsColumn}>
            {Object.keys(monthGroups)
              .sort()
              .map((monthKey) => {
                const monthData = monthGroups[monthKey];
                const dateGroups = this.groupEventsByDate(monthData.events);

                return (
                  <div key={monthKey} className={styles.monthSection}>
                    <MonthHeader month={monthData.month} year={monthData.year} />

                    {Object.keys(dateGroups)
                      .sort()
                      .map((dateKey) => {
                        const dateData = dateGroups[dateKey];

                        return (
                          <div key={dateKey} className={styles.dateSection}>
                            <DateHeader date={dateData.date} />

                            {dateData.events.map((event) => (
                              <LocaleWrapper event={event} key={event.get('id')}>
                                <MonthlyViewEvent
                                  event={event}
                                  settings={settings}
                                  onResizeComponent={onResizeComponent}
                                  onEventRegister={onEventRegister}
                                />
                              </LocaleWrapper>
                            ))}
                          </div>
                        );
                      })}
                  </div>
                );
              })}

            {filteredEvents.size === 0 && (
              <div className={styles.noEvents}>
                <p>No events found for the selected date.</p>
              </div>
            )}
          </Col>

          {/* Right side: Calendar */}
          <Col xs={4} className={styles.calendarColumn}>
            <SubmitEventButton settings={settings} />
            <div className={styles.calendarWrapper}>
              <Calendar
                onChange={this.handleDateClick}
                value={this.state.selectedDate}
                tileClassName={({ date }) => (this.hasEvents(date) ? `react-calendar-has-events ${styles.hasEvents}` : null)}
                className={styles.calendar}
              />
              {this.state.selectedDate && (
                <button className={styles.clearButton} onClick={() => this.setState({ selectedDate: null })}>
                  Show All Events
                </button>
              )}
            </div>
          </Col>
        </Row>
      </div>
    );
  }
}

MonthlyView.propTypes = {
  events: PropTypes.object.isRequired,
  settings: PropTypes.instanceOf(Map).isRequired,
  onResizeComponent: PropTypes.func,
  onEventRegister: PropTypes.func,
};
